/**
 * Solubility products Ksp at 25 °C.
 *
 * Source: the University of Wisconsin–Madison general-chemistry table "Solubility Product
 * Constants for Some Inorganic Compounds at 25 °C"
 * (https://www2.chem.wisc.edu/deptfiles/genchem/tables/KspTable.htm), compiled from
 * R. J. Myers, "The New Low Value for the Second Dissociation Constant for H₂S",
 * J. Chem. Educ. 1986, 63, 687–690. The set is a selection of common salts; metal sulfides
 * are left out because the table's own note says the usual Ksp equation does not apply to
 * them (sulfide is too strong a base). Values from other handbooks differ, sometimes by a
 * factor of two or more (OpenStax Chemistry 2e prints 2.3 × 10⁻⁸ for BaSO₄ where this
 * table has 1.1 × 10⁻¹⁰), so a molar solubility is only as good as the Ksp behind it.
 *
 * Water of hydration does not appear in a Ksp expression; hydrates are listed with their
 * water so that grams per litre use the right molar mass.
 */

export interface KspEntry {
  /** Formula as typed, hydrates joined with "·". */
  formula: string;
  ksp: number;
}

export const KSP_TABLE: readonly KspEntry[] = [
  // Halides
  { formula: "AgCl", ksp: 1.8e-10 },
  { formula: "AgBr", ksp: 3.3e-13 },
  { formula: "AgI", ksp: 1.5e-16 },
  { formula: "PbCl2", ksp: 1.7e-5 },
  { formula: "PbBr2", ksp: 6.3e-6 },
  { formula: "PbI2", ksp: 8.7e-9 },
  { formula: "PbF2", ksp: 3.7e-8 },
  { formula: "CuCl", ksp: 1.9e-7 },
  { formula: "CuBr", ksp: 5.3e-9 },
  { formula: "CuI", ksp: 5.1e-12 },
  { formula: "Hg2Cl2", ksp: 1.1e-18 },
  { formula: "Hg2Br2", ksp: 1.3e-22 },
  { formula: "Hg2I2", ksp: 4.5e-29 },
  { formula: "CaF2", ksp: 3.9e-11 },
  { formula: "MgF2", ksp: 6.4e-9 },
  { formula: "BaF2", ksp: 1.7e-6 },
  // Sulfates
  { formula: "BaSO4", ksp: 1.1e-10 },
  { formula: "SrSO4", ksp: 2.8e-7 },
  { formula: "PbSO4", ksp: 1.8e-8 },
  { formula: "Ag2SO4", ksp: 1.7e-5 },
  { formula: "Hg2SO4", ksp: 6.8e-7 },
  { formula: "CaSO4·2H2O", ksp: 2.4e-5 },
  // Carbonates
  { formula: "CaCO3", ksp: 3.8e-9 },
  { formula: "BaCO3", ksp: 8.1e-9 },
  { formula: "SrCO3", ksp: 9.4e-10 },
  { formula: "FeCO3", ksp: 3.5e-11 },
  { formula: "ZnCO3", ksp: 1.5e-11 },
  { formula: "PbCO3", ksp: 1.5e-13 },
  { formula: "CuCO3", ksp: 2.5e-10 },
  { formula: "MnCO3", ksp: 1.8e-11 },
  { formula: "NiCO3", ksp: 6.6e-9 },
  { formula: "CoCO3", ksp: 8e-13 },
  { formula: "CdCO3", ksp: 2.5e-14 },
  { formula: "Ag2CO3", ksp: 8.1e-12 },
  // Hydroxides
  { formula: "Ca(OH)2", ksp: 7.9e-6 },
  { formula: "Al(OH)3", ksp: 1.9e-33 },
  { formula: "Fe(OH)3", ksp: 6.3e-38 },
  { formula: "Fe(OH)2", ksp: 7.9e-15 },
  { formula: "Cu(OH)2", ksp: 1.6e-19 },
  { formula: "Zn(OH)2", ksp: 4.5e-17 },
  { formula: "Ni(OH)2", ksp: 2.8e-16 },
  { formula: "Co(OH)2", ksp: 2.5e-16 },
  { formula: "Cr(OH)3", ksp: 6.7e-31 },
  { formula: "Pb(OH)2", ksp: 2.8e-16 },
  { formula: "Mn(OH)2", ksp: 4.6e-14 },
  { formula: "Cd(OH)2", ksp: 1.2e-14 },
  // Chromates, phosphates, oxalates
  { formula: "Ag2CrO4", ksp: 9e-12 },
  { formula: "BaCrO4", ksp: 2e-10 },
  { formula: "PbCrO4", ksp: 1.8e-14 },
  { formula: "SrCrO4", ksp: 3.6e-5 },
  { formula: "CaCrO4", ksp: 7.1e-4 },
  { formula: "Ca3(PO4)2", ksp: 1e-25 },
  { formula: "Ag3PO4", ksp: 8.9e-17 },
  { formula: "Pb3(PO4)2", ksp: 3e-44 },
  { formula: "Zn3(PO4)2", ksp: 9.1e-33 },
  { formula: "Ba3(PO4)2", ksp: 1.3e-29 },
  { formula: "MgC2O4", ksp: 8.6e-5 },
  { formula: "CaC2O4·H2O", ksp: 2.3e-9 },
];
